import { useState, useEffect } from 'react';
import { FileText, Image, File, Plus, Trash2, ExternalLink, Clock, User, Upload, Paperclip } from 'lucide-react';
import api from '../../api/config';
import DocumentUploadModal from './DocumentUploadModal';

const DOC_TYPE_LABELS = {
  cotizacion: 'Cotizacion',
  orden_compra_cliente: 'OC Cliente',
  orden_compra_proveedor: 'OC Proveedor',
  guia_despacho: 'Guia Despacho',
  factura_cliente: 'Factura Cliente',
  factura_proveedor: 'Factura Proveedor',
  otro: 'Otro'
};

const DOC_TYPE_COLORS = {
  cotizacion: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
  orden_compra_cliente: { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  orden_compra_proveedor: { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  guia_despacho: { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe' },
  factura_cliente: { bg: '#fce7f3', text: '#9d174d', border: '#fbcfe8' },
  factura_proveedor: { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' },
  otro: { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' }
};

// Mapeo de campos del maestro a tipos de documento
const FIELD_TO_DOC_TYPE = {
  cotizacion: 'cotizacion',
  p_order: 'orden_compra_cliente',
  oc_sobek: 'orden_compra_proveedor',
  guia: 'guia_despacho',
  factura: 'factura_cliente',
  factura_proveedor: 'factura_proveedor'
};

const ModalDocumentsTab = ({ itemNumber, itemData, onDocumentChange }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (itemNumber) loadDocuments();
  }, [itemNumber]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/documents/item/${itemNumber}`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('¿Eliminar este documento?')) return;
    setDeletingId(docId);
    try {
      await api.delete(`/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      onDocumentChange?.();
    } catch (error) {
      alert('Error al eliminar documento');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUploadSuccess = () => {
    loadDocuments();
    onDocumentChange?.();
  };

  const openUploadForType = (docType) => {
    setUploadDocType(docType);
    setShowUploadModal(true);
  };

  const getFileIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return <FileText size={16} style={{ color: '#ef4444' }} />;
    if (mimeType?.startsWith('image/')) return <Image size={16} style={{ color: '#3b82f6' }} />;
    return <File size={16} />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Agrupar docs por tipo
  const docsByType = {};
  documents.forEach(doc => {
    if (!docsByType[doc.doc_type]) docsByType[doc.doc_type] = [];
    docsByType[doc.doc_type].push(doc);
  });

  // Campos del maestro que pueden tener documentos asociados
  const docFields = [
    { key: 'cotizacion', label: 'Cotizacion', docType: 'cotizacion' },
    { key: 'p_order', label: 'Orden Compra Cliente', docType: 'orden_compra_cliente' },
    { key: 'oc_sobek', label: 'OC Proveedor / Sobek', docType: 'orden_compra_proveedor' },
    { key: 'guia', label: 'Guia de Despacho', docType: 'guia_despacho' },
    { key: 'factura', label: 'Factura Cliente', docType: 'factura_cliente' },
    { key: 'factura_proveedor', label: 'Factura Proveedor', docType: 'factura_proveedor' },
  ];

  return (
    <div style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
            Documentos del Item #{itemNumber}
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
            {documents.length} documento{documents.length !== 1 ? 's' : ''} adjunto{documents.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => { setUploadDocType(''); setShowUploadModal(true); }}
          style={{
            padding: '8px 16px',
            background: 'var(--theme-secondary, #000)',
            color: 'var(--theme-primary, #c4fffc)',
            border: '2px solid var(--theme-secondary, #000)',
            borderRadius: '10px', fontSize: '12px', fontWeight: 700,
            cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
            display: 'flex', alignItems: 'center', gap: '6px'
          }}
        >
          <Plus size={14} /> Subir
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>Cargando...</div>
      ) : (
        <>
          {/* Quick upload por tipo de campo */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '8px', marginBottom: '20px'
          }}>
            {docFields.map(field => {
              const fieldValue = itemData?.[field.key];
              const docsForType = docsByType[field.docType] || [];
              const typeColor = DOC_TYPE_COLORS[field.docType];
              return (
                <div
                  key={field.key}
                  style={{
                    border: `1.5px solid ${docsForType.length > 0 ? typeColor.border : '#e5e7eb'}`,
                    borderRadius: '10px',
                    padding: '10px 12px',
                    background: docsForType.length > 0 ? typeColor.bg : '#fafafa',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: '4px'
                  }}>
                    <span style={{
                      fontSize: '11px', fontWeight: 700,
                      color: docsForType.length > 0 ? typeColor.text : '#6b7280'
                    }}>
                      {field.label}
                    </span>
                    {docsForType.length > 0 && (
                      <span style={{
                        fontSize: '10px', fontWeight: 700, background: typeColor.text,
                        color: 'white', borderRadius: '10px', padding: '0 6px',
                        lineHeight: '16px'
                      }}>
                        {docsForType.length}
                      </span>
                    )}
                  </div>
                  {fieldValue && (
                    <div style={{ fontSize: '12px', color: '#374151', fontWeight: 500, marginBottom: '6px' }}>
                      Ref: {fieldValue}
                    </div>
                  )}
                  <button
                    onClick={() => openUploadForType(field.docType)}
                    style={{
                      width: '100%', padding: '5px 8px',
                      background: 'white', border: `1px dashed ${docsForType.length > 0 ? typeColor.border : '#d1d5db'}`,
                      borderRadius: '6px', fontSize: '11px', color: '#6b7280',
                      cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                    }}
                  >
                    <Upload size={11} /> Subir {field.label.toLowerCase()}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Lista de documentos */}
          {documents.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '24px',
              background: '#fafafa', borderRadius: '12px', border: '2px dashed #e5e7eb'
            }}>
              <Paperclip size={32} style={{ color: '#d1d5db', marginBottom: '8px' }} />
              <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                No hay documentos adjuntos a este item
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#d1d5db' }}>
                Usa los botones de arriba para subir por tipo o el boton "Subir"
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {documents.map(doc => {
                const typeColor = DOC_TYPE_COLORS[doc.doc_type] || DOC_TYPE_COLORS.otro;
                return (
                  <div
                    key={doc.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 12px', background: '#fafafa', borderRadius: '10px',
                      border: '1px solid #e5e7eb', transition: 'all 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#d1d5db'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                  >
                    <div style={{
                      width: '34px', height: '34px', borderRadius: '8px',
                      background: 'white', border: '1px solid #e5e7eb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      {getFileIcon(doc.mime_type)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600, fontSize: '12px', color: '#1f2937',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {doc.original_name}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                        <span style={{
                          fontSize: '10px', fontWeight: 700, padding: '1px 6px',
                          borderRadius: '4px', background: typeColor.bg,
                          color: typeColor.text, border: `1px solid ${typeColor.border}`
                        }}>
                          {DOC_TYPE_LABELS[doc.doc_type]}
                        </span>
                        <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                          {formatFileSize(doc.file_size)}
                        </span>
                        <span style={{ fontSize: '10px', color: '#9ca3af' }}>
                          {formatDate(doc.created_at)}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Abrir"
                        style={{
                          width: '28px', height: '28px', borderRadius: '6px',
                          background: '#eff6ff', border: '1px solid #bfdbfe',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          textDecoration: 'none'
                        }}
                      >
                        <ExternalLink size={12} style={{ color: '#2563eb' }} />
                      </a>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        disabled={deletingId === doc.id}
                        title="Eliminar"
                        style={{
                          width: '28px', height: '28px', borderRadius: '6px',
                          background: '#fef2f2', border: '1px solid #fecaca',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: deletingId === doc.id ? 0.5 : 1
                        }}
                      >
                        <Trash2 size={12} style={{ color: '#dc2626' }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        itemNumber={itemNumber}
        itemData={itemData}
        onUploadSuccess={handleUploadSuccess}
        defaultDocType={uploadDocType}
      />
    </div>
  );
};

export default ModalDocumentsTab;
